import React, { useState, useEffect } from 'react';
import '../styles.css';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { logInteraction } from '../utils/tracker.js';
import { getMetadataBundleForStaticPage } from '../metadata/SEOmetadata.js';
import useWindowSize from '../hooks/useWindowSize.js';
import { useLocation, useParams } from 'react-router-dom';
import { getMetadataBundle } from '../metadata/languageMeta.js';
import { t } from '../i18n/translationStrings.js'; // Added import

const languageMap = {
    en: 'English',
    de: 'German',
    fr: 'French',
    es: 'Spanish',
    it: 'Italian',
    pt: 'Portuguese',
    zh: 'Chinese',
    ru: 'Russian',
    sv: 'Swedish',
    da: 'Danish',
    pl: 'Polish',
    fi: 'Finnish'
};

const GenrePage = () => {
    const [genre, setGenre] = useState('Loading...');
    const [books, setBooks] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const windowWidth = useWindowSize();
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedLanguage, setSelectedLanguage] = useState('');
    const booksPerPage = 20;
    const location = useLocation();
    const { genreSlug } = useParams();
    const selectedGenre = genreSlug?.toLowerCase().trim() || 'all';

    const S3_BUCKET_URL = process.env.REACT_APP_S3_BUCKET_URL;
    
    const truncateText = (text, maxLength) => {
        if (!text || text.length <= maxLength) return text;
        return text.slice(0, maxLength).trim() + '...';
    };

    const capitalizeEachWord = (str) =>
        str.replace(/\w\S*/g, w => w.charAt(0).toUpperCase() + w.substr(1));

    useEffect(() => {
        const displayName = selectedGenre === 'all' ? 'All Genres' : capitalizeFirstLetter(selectedGenre);
        setGenre(displayName);
        logInteraction('genre_view', { genreName: selectedGenre });

        loadBooksByGenre(selectedGenre);
    }, [selectedLanguage, selectedGenre]);

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedLanguage]);

    const capitalizeFirstLetter = (string) => {
        return string.charAt(0).toUpperCase() + string.slice(1);
    };

    const fetchMetadata = async () => {
        try {
            if (!S3_BUCKET_URL) throw new Error('Missing REACT_APP_S3_BUCKET_URL');
            const bucketResponse = await fetch(`${S3_BUCKET_URL}/HermesTest/metadata.json`);
            const publicResponse = await fetch('/ProductMetaData.json');
            if (!bucketResponse.ok) throw new Error(`S3 ${bucketResponse.status}`);
            const bucketBooks = await bucketResponse.json();

            const publicBooks = publicResponse.ok ? await publicResponse.json() : [];
            const bucketBooksWithSource = bucketBooks.map(book => ({ ...book, source: 's3' }));
            // Mark public data with source: 'public'
            const publicBooksWithSource = publicBooks.map(book => ({ ...book, source: 'public' }));

            return [...bucketBooksWithSource, ...publicBooksWithSource];
        } catch (error) {
            console.error("Error fetching metadata:", error);
            return [];
        }
    };

    const loadBooksByGenre = async (selectedGenre) => {
        selectedGenre = selectedGenre.toLowerCase().trim();
        if (selectedGenre === 'all genres') selectedGenre = 'all';
        
        setIsLoading(true);
        try {
            const metadata = await fetchMetadata();
            metadata.forEach(book => {
                if (!book.titleSlug) {
                    book.titleSlug = book.title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                }
            });

            const filteredBooks = metadata.filter(book => {
                const genreMatch = selectedGenre === 'all'
                    ? true
                    : book.categories.some(category =>
                        category.toLowerCase().trim().includes(selectedGenre)
                    );
                const languageMatch = selectedLanguage
                    ? book.language.toLowerCase() === selectedLanguage.toLowerCase()
                    : true;
                return genreMatch && languageMatch;
            });

            setBooks(filteredBooks);
        } catch (error) {
            console.error("Error loading books by genre:", error);
            setBooks([]);
        } finally {
            setIsLoading(false);
        }
    };

    const indexOfLastBook = currentPage * booksPerPage;
    const indexOfFirstBook = indexOfLastBook - booksPerPage;
    const currentBooks = books.slice(indexOfFirstBook, indexOfLastBook);
    const totalPages = Math.ceil(books.length / booksPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const gridStyle = {
        display: 'grid',
        gridTemplateColumns: windowWidth < 768 ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
        gap: '20px',
        padding: '1rem',
        maxWidth: '1200px',
        margin: '0 auto',
    };

    const paginationContainerStyle = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: '20px',
        width: '100%',
    };

    const paginationStyle = {
        display: 'flex',
        gap: '10px'
    };

    const paginationButtonStyle = (isActive) => ({
        padding: '0.5rem 1rem',
        backgroundColor: isActive ? 'var(--highlight-color)' : 'transparent',
        color: isActive ? '#fff' : 'var(--text-color)',
        border: '1px solid var(--highlight-color)',
        borderRadius: '4px',
        cursor: 'pointer',
    });
    const metadata = getMetadataBundle('genre', selectedLanguage || 'en', genre, {
        url: `https://www.hermesreads.com/genre/${genreSlug}`,
        image: '/images/PlaceholderJPG.webp'
    });
    return (
        <main className="shop-page">
                    
            <Helmet>
                <html lang={metadata.htmlLang} />
                <title>{metadata.title}</title>
                <meta name="description" content={metadata.description} />
                <meta name="keywords" content={metadata.keywords} />

                <meta property="og:title" content={metadata.og.title} />
                <meta property="og:description" content={metadata.og.description} />
                <meta property="og:image" content={metadata.og.image} />
                <meta property="og:type" content={metadata.og.type} />
                <meta property="og:url" content={metadata.og.url} />

                <meta name="twitter:card" content={metadata.twitter.card} />
                <meta name="twitter:title" content={metadata.twitter.title} />
                <meta name="twitter:description" content={metadata.twitter.description} />
                <meta name="twitter:image" content={metadata.twitter.image} />

                <link rel="canonical" href={metadata.og.url} />
                <script type="application/ld+json">{JSON.stringify(metadata.structuredData)}</script>
            </Helmet>

            <section className="shop-intro">
                <h2 id="genre-title">
                    {t("booksIn")} {t(capitalizeEachWord(selectedGenre))}
                </h2>
                <div style={{ marginTop: '1rem', marginBottom: '1rem', display: 'flex', justifyContent: 'flex-start' }}>
                    <select
                        value={selectedLanguage}
                        onChange={(e) => setSelectedLanguage(e.target.value)}
                        style={{
                            padding: '0.5rem',
                            border: '1px solid var(--dark-gray)',
                            borderRadius: '4px',
                            backgroundColor: 'var(--light-gray)',
                            color: 'var(--text-color)'
                        }}
                    >
                        <option value="">All Languages</option>
                        {Object.entries(languageMap).map(([code, name]) => (
                            <option key={code} value={code}>{name}</option>
                        ))}
                    </select>
                </div>
            </section>

            <section className="shop-collection">
                {isLoading ? (
                    <p>Loading books...</p>
                ) : (
                    <>
                        <div id="product-grid" style={gridStyle}>
                            {currentBooks.length > 0 ? (
                                currentBooks.map(book => {
                                    const join = (base, path) => {
                                        if (!path) return '';
                                        return path.startsWith('http')
                                            ? path
                                            : `${base}${path.startsWith('/') ? '' : '/'}${path}`;
                                    };
                                    // If source === 'public', use Hermes44
                                    // If source === 's3', use real or placeholder
                                    const coverImage = book.source === 'public'
                                        ? '/images/Hermes44.webp'
                                        : (book.cover ? join(S3_BUCKET_URL, book.cover) : '/images/PlaceholderJPG.webp');

                                    return (
                                        <Link
                                            key={book.id || book.titleSlug || book.title}
                                            to={`/book/${book.titleSlug || book.id}`}
                                            style={{ textDecoration: 'none', color: 'inherit' }}
                                        >
                                            {/*
                                            // 🧩 Restore after Google reset:
                                            to={book.source === 'public'
                                                ? `/productpage/${book.id}`
                                                : `/book/${book.titleSlug || book.id}`}
                                            */}
                                            <div
                                                className="thumbnail"
                                                style={{
                                                    position: 'relative',           // ✅ fix
                                                    zIndex: 1,                     
                                                    border: '1px solid var(--highlight-color)',
                                                    borderRadius: '8px',
                                                    padding: '1rem',
                                                    margin: '0.5rem',
                                                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                                    backgroundColor: 'var(--light-gray)',
                                                }}
                                            >
                                                <div
                                                    className="cover"
                                                    style={{
                                                        backgroundImage: `url('${coverImage}')`,
                                                        backgroundSize: 'cover',
                                                        backgroundPosition: 'center',
                                                        height: '200px',
                                                        borderRadius: '4px',
                                                        marginBottom: '1rem',
                                                        pointerEvents: 'none',
                                                        position: 'relative',
                                                        zIndex: 0,
                                                    }}
                                                />
                                                <h2
                                                    className="product-title"
                                                    style={{
                                                        fontSize: windowWidth < 768 ? '0.9rem' : '1rem',
                                                        marginBottom: '0.5rem',
                                                        textAlign: 'center'
                                                    }}
                                                >
                                                    {truncateText(book.title, 50)}
                                                </h2>
                                                <p
                                                    className="author"
                                                    style={{
                                                        fontSize: windowWidth < 768 ? '0.8rem' : '0.9rem',
                                                        color: 'var(--text-color)',
                                                        textAlign: 'center'
                                                    }}
                                                >
                                                    {truncateText(book.author, 33)}
                                                </p>
                                            </div>
                                        </Link>
                                    );
                                })
                            ) : (
                                <p>No books found for this genre.</p>
                            )}
                        </div>

                        {!isLoading && totalPages > 1 && (
                            <div className="pagination-container" style={paginationContainerStyle}>
                                <div className="pagination" style={paginationStyle}>
                                    {(() => {
                                        const buttons = [];
                                        const delta = 2;

                                        for (let i = 1; i <= totalPages; i++) {
                                            // Always show first, last, current, and +/- delta pages
                                            if (
                                                i === 1 ||
                                                i === totalPages ||
                                                (i >= currentPage - delta && i <= currentPage + delta)
                                            ) {
                                                buttons.push(
                                                    <button
                                                        key={i}
                                                        onClick={() => paginate(i)}
                                                        className={currentPage === i ? 'active' : ''}
                                                        style={paginationButtonStyle(currentPage === i)}
                                                    >
                                                        {i}
                                                    </button>
                                                );
                                            } else if (
                                                // Add ellipses only once between gaps
                                                (i === currentPage - delta - 1 || i === currentPage + delta + 1)
                                            ) {
                                                buttons.push(<span key={`ellipsis-${i}`} style={{ padding: '0 0.5rem', color: 'var(--text-color)' }}>...</span>);
                                            }
                                        }

                                        return buttons;
                                    })()}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </section>
        </main>
    );
};

export default GenrePage;
